import {
  DEFAULT_MATRIX_PIVOT,
  MATRIX_PIVOTS,
  MATRIX_PIVOT_LABELS,
  MATRIX_PIVOT_MEANINGS,
  type MatrixPivot,
  type SingularityLevier,
  countPivotFilledSteps,
} from '@mss_creator/shared';
import { OptionTile, Tooltip } from '@pms-digital/design-system';
import { useId } from 'react';

/** A pivot is selectable only when every one of the setup's steps fills it. */
const SETUP_STEP_COUNT = 6;

export interface PivotSelectorProps {
  value: MatrixPivot;
  setup: readonly SingularityLevier[];
  companyId: string;
  /** Frozen since the first generation: shown, never changed. */
  readOnly: boolean;
  /** The read-only state comes from an archived company, not from the freeze: says so. */
  archived?: boolean;
  onChange: (pivot: MatrixPivot) => void;
}

function sentence(meaning: string): string {
  return `${meaning.charAt(0).toUpperCase()}${meaning.slice(1)}.`;
}

/**
 * The wizard's pivot choice (issue #16): one radio tile per pivot, in
 * `MATRIX_PIVOTS` order, each tile carrying what the pivot means as its
 * description.
 *
 * A pivot blank on any setup step is refused by the backend (422), so its
 * tile is unavailable here — `aria-disabled` rather than `disabled`, which
 * would make it unfocusable and its tooltip unreachable from the keyboard. Its
 * rich tooltip says how many steps fill it and links to the company sheet,
 * where the setup is edited.
 */
export function PivotSelector({
  value,
  setup,
  companyId,
  readOnly,
  archived = false,
  onChange,
}: PivotSelectorProps) {
  const headingId = useId();

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 id={headingId} className="font-semibold text-body-lg">
          Pivot de la matrice
        </h3>
        <span className="text-gray-600 text-small">
          {archived
            ? "Non modifiable : l'entreprise est archivée."
            : readOnly
              ? 'Figé depuis la première génération. Pour changer, créez une nouvelle matrice depuis celle-ci.'
              : "Le champ du setup sur lequel chaque ligne de la matrice est construite. Modifiable jusqu'à la génération."}
        </span>
      </div>
      <div
        role="radiogroup"
        aria-labelledby={headingId}
        aria-readonly={readOnly || undefined}
        className="grid grid-cols-1 gap-2 sm:grid-cols-2"
      >
        {MATRIX_PIVOTS.map((pivot) => {
          const checked = pivot === value;
          const filledSteps = countPivotFilledSteps(setup, pivot);
          const available = filledSteps >= SETUP_STEP_COUNT;
          const unavailable = !available && !checked;
          const locked = readOnly || unavailable;
          const label =
            pivot === DEFAULT_MATRIX_PIVOT
              ? `${MATRIX_PIVOT_LABELS[pivot]} (par défaut)`
              : MATRIX_PIVOT_LABELS[pivot];
          const tile = (
            <OptionTile
              type="radio"
              name={`${headingId}-pivot`}
              value={pivot}
              label={label}
              description={
                unavailable
                  ? `Indisponible : renseigné pour ${filledSteps} étapes sur ${SETUP_STEP_COUNT}.`
                  : sentence(MATRIX_PIVOT_MEANINGS[pivot])
              }
              checked={checked}
              aria-disabled={locked || undefined}
              onClick={(event) => {
                if (locked) event.preventDefault();
              }}
              onChange={() => {
                if (locked || checked) return;
                onChange(pivot);
              }}
            />
          );

          if (unavailable) {
            return (
              <Tooltip
                key={pivot}
                title="Pivot indisponible"
                content={`Renseigné pour ${filledSteps} étapes sur ${SETUP_STEP_COUNT} - complétez le setup de l'entreprise`}
                action={{ label: 'Ouvrir la fiche entreprise', href: `/companies/${companyId}` }}
              >
                <div>{tile}</div>
              </Tooltip>
            );
          }
          return <div key={pivot}>{tile}</div>;
        })}
      </div>
    </div>
  );
}
